import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  Trash2,
  Eye,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API = API_BASE;

const CreditReportAnalysisList = () => {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sourceNotes, setSourceNotes] = useState('');

  // Bureau is hardcoded to Equifax (system standardized on Equifax iSoftpull)
  const FIXED_SOURCE_PROVIDER = 'equifax';

  useEffect(() => {
    fetchAnalyses();
    fetchClients();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/credit-report-analyses`);
      setAnalyses(response.data);
    } catch (error) {
      toast.error('Error al cargar análisis');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.log('No clients endpoint available');
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/credit-report-analyses`, {
        source_provider: FIXED_SOURCE_PROVIDER,
        client_id: selectedClientId === 'none' ? null : selectedClientId || null,
        source_notes: sourceNotes || null
      });
      toast.success('Análisis creado');
      navigate(`/apps/credit-report/${response.data.id}`);
    } catch (error) {
      toast.error('Error al crear análisis');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este análisis?')) return;
    
    try {
      await axios.delete(`${API}/credit-report-analyses/${id}`);
      toast.success('Análisis eliminado');
      fetchAnalyses();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'clean_credit_review':
        return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"><CheckCircle className="w-3 h-3 mr-1" />Clean Credit Review</Badge>;
      case 'review_required':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertTriangle className="w-3 h-3 mr-1" />Review Required</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Análisis de Reporte de Crédito</h1>
          <p className="text-muted-foreground">Soft Pull - Detección de eventos de alto riesgo</p>
        </div>
      </div>

      {/* Create New Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Nuevo Análisis de Crédito
          </CardTitle>
          <CardDescription>Cargue un reporte de crédito (Soft Pull) para análisis automático</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Buró de Crédito</Label>
              <div className="flex items-center gap-2 mt-2 p-2 border rounded-md bg-muted/30">
                <Badge className="bg-red-600 text-white text-base px-3 py-1" data-testid="bureau-badge-equifax">EQUIFAX</Badge>
                <span className="text-sm text-muted-foreground">Estandarizado en iSoftpull Equifax</span>
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1">
                <User className="w-3 h-3" />
                Vincular Cliente (Opcional)
              </Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger data-testid="client-select-trigger">
                  <SelectValue placeholder="Sin cliente vinculado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin cliente vinculado</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.full_name || client.company_name || client.name || 'Cliente sin nombre'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mb-4">
            <Label>Notas (Opcional)</Label>
            <Input 
              placeholder="Notas adicionales sobre este análisis..."
              value={sourceNotes}
              onChange={(e) => setSourceNotes(e.target.value)}
              data-testid="source-notes-input"
            />
          </div>
          <Button onClick={handleCreate} disabled={creating} data-testid="create-analysis-btn">
            <Plus className="w-4 h-4 mr-2" />
            {creating ? 'Creando...' : 'Crear Análisis'}
          </Button>
        </CardContent>
      </Card>

      {/* List of Analyses */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Análisis Recientes</h2>
        
        {analyses.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay análisis de crédito registrados</p>
              <p className="text-sm">Cree uno nuevo desde el formulario arriba</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {analyses.map(analysis => (
              <Card 
                key={analysis.id} 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate(`/apps/credit-report/${analysis.id}`)}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">
                          {analysis.pdf_filename || 'Sin archivo'}
                        </span>
                        {analysis.bureau && (
                          <Badge className={analysis.is_legacy ? "bg-amber-200 text-amber-900 border border-amber-400" : "bg-red-600 text-white"} data-testid={`bureau-badge-${analysis.id}`}>
                            {analysis.is_legacy ? `Legacy: ${analysis.bureau}` : analysis.bureau}
                          </Badge>
                        )}
                        {analysis.decision_tag?.tag && (
                          <Badge
                            style={{ backgroundColor: analysis.decision_tag.color_hex, color: '#fff' }}
                            data-testid={`decision-tag-${analysis.id}`}
                          >
                            {analysis.decision_tag.label || analysis.decision_tag.tag}
                          </Badge>
                        )}
                        {getStatusBadge(analysis.status)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {analysis.applicant?.name && <>Solicitante: <span className="font-medium">{analysis.applicant.name}</span> • </>}
                        Creado: {new Date(analysis.created_at).toLocaleDateString()}
                      </div>
                      {analysis.high_risk_count > 0 && (
                        <div className="text-sm text-red-600 dark:text-red-400 font-medium">
                          ⚠️ {analysis.high_risk_count} evento(s) de alto riesgo detectados
                        </div>
                      )}
                      {analysis.status === 'clean_credit_review' && (
                        <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                          ✅ Sin eventos de alto riesgo
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => handleDelete(analysis.id, e)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditReportAnalysisList;
